import {
  formatWorkspaceMoney,
  titleCaseLabel,
} from "./workspacePresentation";

function toNumber(value, fallback = 0) {
  const amount = Number(value);
  return Number.isFinite(amount) ? amount : fallback;
}

export function getWorkspaceFinanceSnapshot(record = {}) {
  const finance = record?.invoice_metadata?.workspace_finance || {};
  const pricing = record?.pricing_snapshot_json || {};
  const selectedAddons = Array.isArray(record?.selected_addons)
    ? record.selected_addons
    : Array.isArray(finance?.selected_addons)
    ? finance.selected_addons
    : Array.isArray(pricing?.selected_addons)
    ? pricing.selected_addons
    : [];

  const baseAmount = toNumber(
    finance.base_amount ?? record?.base_amount ?? pricing?.base_amount
  );
  const addonsTotalAmount = toNumber(
    finance.addons_total_amount ??
      record?.addons_total_amount ??
      pricing?.addons_total_amount
  );
  const cleaningFee = toNumber(finance.cleaning_fee ?? pricing?.cleaning_fee);
  const depositAmount = toNumber(
    finance.deposit_amount ?? record?.deposit_amount ?? pricing?.deposit_amount
  );
  const estimatedTotal = toNumber(
    finance.estimated_total ?? record?.total_amount,
    baseAmount + addonsTotalAmount + cleaningFee
  );
  const amountDueNow = toNumber(
    finance.amount_due_now,
    depositAmount > 0 ? depositAmount : 0
  );
  const amountDueLater = toNumber(
    finance.amount_due_later,
    Math.max(estimatedTotal - amountDueNow, 0)
  );

  return {
    workspaceName:
      finance.workspace_name || record?.workspace_name || null,
    workspaceCode:
      finance.workspace_code || record?.workspace_code || null,
    salonName: finance.salon_name || null,
    occupancyMode: finance.occupancy_mode || record?.occupancy_mode || null,
    clientOwnershipMode:
      finance.client_ownership_mode || record?.client_ownership_mode || null,
    ownerUpgradeRequired: Boolean(finance.owner_upgrade_required),
    salonJoinRequestId: finance.salon_join_request_id ?? record?.salon_join_request_id ?? null,
    financeStateCode:
      finance.finance_state_code || record?.finance_state_code || null,
    marketplaceBookingId:
      finance.marketplace_booking_id || record?.marketplace_booking_id || null,
    baseAmount,
    addonsTotalAmount,
    cleaningFee,
    depositAmount,
    estimatedTotal,
    amountDueNow,
    amountDueLater,
    paymentTiming: finance.payment_timing || "",
    selectedAddons,
  };
}

export function getWorkspacePaymentState(record = {}) {
  const financeStateCode = String(
    record?.invoice_metadata?.workspace_finance?.finance_state_code ||
      record?.finance_state_code ||
      ""
  ).toLowerCase();
  const paymentStatus = String(record?.payment_status || "").toLowerCase();
  const bookingStatus = String(record?.status || "").toLowerCase();

  if (financeStateCode === "deposit_held") {
    return {
      label: "Deposit Held",
      tone: "accent",
      message:
        "Payment has been captured and the deposit remains held under the workspace commerce workflow.",
    };
  }
  if (financeStateCode === "payout_pending") {
    return {
      label: "Payout Pending",
      tone: "success",
      message:
        "Payment is captured and owner payout remains pending until the release workflow completes.",
    };
  }
  if (financeStateCode === "payout_on_hold") {
    return {
      label: "Payout On Hold",
      tone: "warning",
      message:
        "Payment is captured, but payout is paused while finance review or dispute handling is still open.",
    };
  }
  if (financeStateCode === "refunded") {
    return {
      label: "Refunded",
      tone: "danger",
      message: "Refund activity has been recorded against this workspace booking.",
    };
  }
  if (financeStateCode === "disputed") {
    return {
      label: "Disputed",
      tone: "warning",
      message:
        "This booking is under finance dispute review, so payout and release actions remain paused.",
    };
  }
  if (paymentStatus === "approval_pending") {
    return {
      label: "Pending Approval",
      tone: "warning",
      message:
        "The salon must approve this booking before any deposit or payment is collected.",
    };
  }
  if (paymentStatus === "deposit_due") {
    return {
      label: "Deposit Due",
      tone: "accent",
      message:
        "A deposit is due now. The remaining balance is coordinated by the salon before occupancy.",
    };
  }
  if (paymentStatus === "payment_pending") {
    return {
      label: "Payment Pending",
      tone: "neutral",
      message:
        bookingStatus === "approved" || bookingStatus === "confirmed"
          ? "The booking is active, and the salon will confirm payment collection timing."
          : "Payment collection timing will be confirmed by the salon.",
    };
  }
  if (paymentStatus === "cancelled") {
    return {
      label: "Cancelled",
      tone: "danger",
      message: "No payment is due because this booking was cancelled.",
    };
  }
  if (paymentStatus === "paid" || paymentStatus === "paid_in_full") {
    return {
      label: "Paid",
      tone: "success",
      message: "Payment is recorded for this booking.",
    };
  }
  return {
    label: titleCaseLabel(record?.payment_status, "Pending"),
    tone: "neutral",
    message: "Payment state will be confirmed by the salon.",
  };
}

export function paymentToneClasses(tone = "neutral") {
  if (tone === "success") {
    return "border-emerald-200 bg-emerald-50 text-emerald-700";
  }
  if (tone === "danger") {
    return "border-rose-200 bg-rose-50 text-rose-700";
  }
  if (tone === "accent") {
    return "border-sky-200 bg-sky-50 text-sky-700";
  }
  if (tone === "warning") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  return "border-slate-200 bg-slate-100 text-slate-700";
}

export function getWorkspaceFinanceRows(record = {}, currency = "CAD") {
  const finance = getWorkspaceFinanceSnapshot(record);
  return [
    {
      label: "Base",
      value: formatWorkspaceMoney(finance.baseAmount, currency),
    },
    {
      label: "Add-ons",
      value: formatWorkspaceMoney(finance.addonsTotalAmount, currency),
    },
    {
      label: "Cleaning fee",
      value: formatWorkspaceMoney(finance.cleaningFee, currency),
    },
    {
      label: "Deposit",
      value: finance.depositAmount
        ? formatWorkspaceMoney(finance.depositAmount, currency)
        : "Not required",
    },
    {
      label: "Estimated total",
      value: formatWorkspaceMoney(finance.estimatedTotal, currency),
    },
    {
      label: "Due now",
      value: formatWorkspaceMoney(finance.amountDueNow, currency),
    },
    {
      label: "Due later",
      value: formatWorkspaceMoney(finance.amountDueLater, currency),
    },
  ];
}
